"use client";

import { ThemeProvider } from "@offstreetparking/design-system/providers";
import type { ColorRamp } from "@offstreetparking/domain/value/branding";
import { type ComponentType, type ReactNode, createContext, useContext, useMemo } from "react";
import type { RouterOutputs } from "../api/types";
import type { PaymentErrorHandler, PaymentFormProps } from "../components/CreatePermitWizard/components/PaymentForm";

export type { PaymentFormProps } from "../components/CreatePermitWizard/components/PaymentForm";

type PaymentSessionResult = RouterOutputs["paymentSession"]["createPaymentSessionPublic"];

export type FlowShellProps = {
	children: ReactNode;
	infoSlot?: ReactNode;
	returnToLink?: ReactNode;
	srOnlyTitle?: string;
	variant?: "default" | "account" | "auth";
	companyLogo?: { url: string; altText: string };
	isLoading?: boolean;
	eventId?: string;
	locationId?: string;
	tenantId?: number;
	hideTopBar?: boolean;
	hideParkerSetup?: boolean;
};

export type FlowPaymentGatewayPanelProps = {
	paymentSession: PaymentSessionResult | undefined;
	formId: string;
	locationLookupId?: string;
	onSuccess: (publicIdentifier: string) => void;
	onError: PaymentErrorHandler;
	setIsProcessing: (isProcessing: boolean) => void;
};

export type FlowThemeProviderProps = {
	branding:
		| {
				id: number;
				primaryColorRamp: ColorRamp;
				accentColorRamp: ColorRamp;
		  }
		| null
		| undefined;
};

const DefaultShell = ({ children, infoSlot, srOnlyTitle }: FlowShellProps) => (
	<div className="flex min-h-screen flex-col">
		<main className="flex h-full grow justify-center gap-16 lg:p-16">
			{srOnlyTitle && <h1 className="sr-only xl:hidden">{srOnlyTitle}</h1>}
			{infoSlot && <div className="hidden w-[475px] justify-center xl:flex print:hidden">{infoSlot}</div>}
			<div id="main-content" tabIndex={-1} className="contents">
				{children}
			</div>
		</main>
	</div>
);

export type PortalFlowHostValue = {
	Shell: (props: FlowShellProps) => ReactNode;
	PaymentSlot: ComponentType<PaymentFormProps> | null;
	PaymentGatewayPanel: ComponentType<FlowPaymentGatewayPanelProps> | null;
	SignInPrompt: ComponentType | null;
	Theme: ComponentType<FlowThemeProviderProps>;
};

const defaultHost: PortalFlowHostValue = {
	Shell: DefaultShell,
	PaymentSlot: null,
	PaymentGatewayPanel: null,
	SignInPrompt: null,
	Theme: ThemeProvider,
};

const PortalFlowHostContext = createContext<PortalFlowHostValue>(defaultHost);

export const PortalFlowHostProvider = ({
	shell,
	paymentSlot,
	paymentGatewayPanel,
	signInPrompt,
	theme,
	children,
}: Partial<{
	shell: (props: FlowShellProps) => ReactNode;
	paymentSlot: ComponentType<PaymentFormProps>;
	paymentGatewayPanel: ComponentType<FlowPaymentGatewayPanelProps>;
	signInPrompt: ComponentType;
	theme: ComponentType<FlowThemeProviderProps>;
}> & { children: ReactNode }) => {
	const value = useMemo(
		() => ({
			Shell: shell ?? defaultHost.Shell,
			PaymentSlot: paymentSlot ?? defaultHost.PaymentSlot,
			PaymentGatewayPanel: paymentGatewayPanel ?? defaultHost.PaymentGatewayPanel,
			SignInPrompt: signInPrompt ?? defaultHost.SignInPrompt,
			Theme: theme ?? defaultHost.Theme,
		}),
		[shell, paymentSlot, paymentGatewayPanel, signInPrompt, theme],
	);

	return <PortalFlowHostContext.Provider value={value}>{children}</PortalFlowHostContext.Provider>;
};

export const usePortalFlowHost = () => useContext(PortalFlowHostContext);

export const CommonLayout = (props: FlowShellProps) => {
	const { Shell } = usePortalFlowHost();
	return <Shell {...props} />;
};

export const FlowThemeProvider = (props: FlowThemeProviderProps) => {
	const { Theme } = usePortalFlowHost();
	return <Theme {...props} />;
};
